import React, { useCallback, useEffect, useMemo, useState } from "react";
import EpsfPageBreadcrumb from "./EpsfPageBreadcrumb";
import ReportTable from "../../common/ReportTable/ReportTable";
import Dropdown from "../../common/dropDown";
import { getUserData } from "../../../services/apiService";
import { EpsfAccessGate, useEpsfAccess } from "./epsfAccess";
import { findOption, useEpsfStudySite } from "./epsfLedgerUi";
import {
  assignDuty,
  fetchDuties,
  fetchDutyAssignments,
  revokeDutyAssignment,
} from "./epsfService";
import "./EpsfPages.scss";

const DUTY_FIELD_OPTIONS = [
  { key: "DutyCode", label: "Code" },
  { key: "DutyName", label: "Name" },
  { key: "ModuleScope", label: "Scope" },
];

const DUTY_DEFAULT_COLUMNS = ["DutyCode", "DutyName", "ModuleScope"];

const ASSIGNMENT_FIELD_OPTIONS = [
  { key: "Duty", label: "Duty" },
  { key: "StaffId", label: "Staff" },
  { key: "StudyId", label: "Study" },
  { key: "SiteId", label: "Site" },
  { key: "ValidFrom", label: "From" },
];

const ASSIGNMENT_DEFAULT_COLUMNS = [
  "Duty",
  "StaffId",
  "StudyId",
  "SiteId",
  "ValidFrom",
];

export default function EpsfDoA() {
  const { loading, canView, moduleAccess, isSystemAdmin } = useEpsfAccess();
  const allow = canView && moduleAccess;
  const ctx = useEpsfStudySite();

  const [duties, setDuties] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const [dutyId, setDutyId] = useState("");
  const [assigneeStaffId, setAssigneeStaffId] = useState("");

  const dutyOptions = useMemo(
    () =>
      duties.map((d) => {
        const id = String(d.DutyId || d.dutyId);
        return {
          value: id,
          label: `${d.DutyCode || d.dutyCode} - ${d.DutyName || d.dutyName}`,
        };
      }),
    [duties],
  );

  const dutyOptionsWithPlaceholder = useMemo(
    () => [{ value: "", label: "Select duty" }, ...dutyOptions],
    [dutyOptions],
  );

  const refresh = useCallback(async () => {
    setError("");
    setListLoading(true);
    try {
      const [d, a] = await Promise.all([
        fetchDuties(),
        fetchDutyAssignments({ StudyId: ctx.studyId || null, IsActive: true }),
      ]);
      if (d.success) setDuties(d.rows || []);
      else if (d.message) setError(d.message);
      if (a.success) setAssignments(a.rows || []);
    } finally {
      setListLoading(false);
    }
  }, [ctx.studyId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const user = getUserData() || {};
    if (!assigneeStaffId) {
      setAssigneeStaffId(String(user.StaffId || user.staffId || ""));
    }
  }, [assigneeStaffId]);

  const onAssign = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    if (!dutyId || !assigneeStaffId) {
      setError("Select a duty and staff id.");
      return;
    }
    setBusy(true);
    try {
      const result = await assignDuty({
        DutyId: dutyId,
        StaffId: assigneeStaffId.trim(),
        StudyId: ctx.studyId || null,
        SiteId: ctx.siteId || null,
        Notes: "Assigned from ePSF Digital DoA",
      });
      if (!result.success) {
        setError(result.message || "Assign failed.");
        return;
      }
      setMessage(result.message || "Duty assigned.");
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const onRevoke = useCallback(
    async (row) => {
      const id = row.DutyAssignmentId || row.dutyAssignmentId;
      setBusy(true);
      try {
        const result = await revokeDutyAssignment(id);
        if (!result.success) setError(result.message || "Revoke failed.");
        else {
          setMessage(result.message || "Assignment revoked.");
          await refresh();
        }
      } finally {
        setBusy(false);
      }
    },
    [refresh],
  );

  const renderDutyCell = useCallback((row, field) => {
    if (field === "DutyCode") return row.DutyCode || row.dutyCode || "—";
    if (field === "DutyName") return row.DutyName || row.dutyName || "—";
    if (field === "ModuleScope") {
      return row.ModuleScope || row.moduleScope || "—";
    }
    const value = row[field];
    return value == null || value === "" ? "—" : String(value);
  }, []);

  const renderAssignmentCell = useCallback((row, field) => {
    if (field === "Duty") {
      return (
        `${row.DutyCode || row.dutyCode || ""} - ${
          row.DutyName || row.dutyName || ""
        }`.trim() || "—"
      );
    }
    if (field === "StaffId") return row.StaffId || row.staffId || "—";
    if (field === "StudyId") return row.StudyId || row.studyId || "All";
    if (field === "SiteId") return row.SiteId || row.siteId || "All";
    if (field === "ValidFrom") {
      return row.ValidFrom
        ? new Date(row.ValidFrom).toLocaleDateString()
        : "—";
    }
    const value = row[field];
    return value == null || value === "" ? "—" : String(value);
  }, []);

  const assignmentRowActions = useMemo(
    () => [
      {
        label: "Revoke",
        icon: "fas fa-ban",
        onClick: (row) => onRevoke(row),
      },
    ],
    [onRevoke],
  );

  return (
    <EpsfAccessGate allow={allow} loading={loading}>
      <div className="epsf-list-page">
        <EpsfPageBreadcrumb
          items={[
            { label: "ePSF", path: "/epsf/imp/inventory" },
            { label: "Digital DoA" },
          ]}
        />
        <div className="epsf-receive">
          <p className="epsf-hub__hint" style={{ marginTop: 0 }}>
            Platform duties consumed by ePSF. Until assignments exist for a duty, IMP actions allow
            staff with ePSF permissions (interim). After you assign, only delegated staff may
            receive / dispense / destroy.
            {!isSystemAdmin ? " Assignments typically managed by pharmacy leads / admins." : null}
          </p>
          {error ? <p className="epsf-receive__error">{error}</p> : null}
          {message ? <p className="epsf-receive__ok">{message}</p> : null}

          <form className="epsf-receive__form" onSubmit={onAssign}>
            <h3>Assign duty</h3>
            <div className="row g-3">
              <div className="col-12">
                <div className="epsf-form-field">
                  <label className="form-label">Duty</label>
                  <Dropdown
                    options={dutyOptionsWithPlaceholder}
                    selected={findOption(dutyOptionsWithPlaceholder, dutyId)}
                    onSelect={(opt) => setDutyId(opt?.value || "")}
                    showSearch
                  />
                </div>
              </div>
              <div className="col-12">
                <div className="epsf-form-field">
                  <label className="form-label">Staff id (GUID)</label>
                  <input
                    className="form-control"
                    value={assigneeStaffId}
                    onChange={(ev) => setAssigneeStaffId(ev.target.value)}
                    required
                  />
                </div>
              </div>
            </div>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? "Saving..." : "Assign duty"}
            </button>
          </form>

          <div className="epsf-report-wrap">
            <h3>Duty catalogue</h3>
            <ReportTable
              rows={duties}
              loading={listLoading}
              fieldOptions={DUTY_FIELD_OPTIONS}
              defaultColumns={DUTY_DEFAULT_COLUMNS}
              getFieldLabel={(field) =>
                DUTY_FIELD_OPTIONS.find((x) => x.key === field)?.label || field
              }
              renderCell={renderDutyCell}
              getRowKey={(row, index) =>
                String(row.DutyId || row.dutyId || index)
              }
              persistLayoutKey="epsf-doa-duties-v1"
              emptyMessage="No duties configured."
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </div>

          <div className="epsf-report-wrap">
            <h3>Active assignments</h3>
            <ReportTable
              rows={assignments}
              loading={listLoading}
              fieldOptions={ASSIGNMENT_FIELD_OPTIONS}
              defaultColumns={ASSIGNMENT_DEFAULT_COLUMNS}
              getFieldLabel={(field) =>
                ASSIGNMENT_FIELD_OPTIONS.find((x) => x.key === field)?.label ||
                field
              }
              renderCell={renderAssignmentCell}
              rowActions={assignmentRowActions}
              getRowKey={(row, index) =>
                String(row.DutyAssignmentId || row.dutyAssignmentId || index)
              }
              persistLayoutKey="epsf-doa-assignments-v1"
              emptyMessage="No assignments - interim permission mode is active."
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </div>
        </div>
      </div>
    </EpsfAccessGate>
  );
}
